import { supabase } from './supabase';

/**
 * Fonte única do link que abre o PDF do boleto de uma fatura.
 *
 * São três origens possíveis, em ordem de preferência:
 *
 * 1. `url_boleto_combinado` — boleto único da parceira (Gran) publicado no site
 *    institucional pela edge function `gerar-boleto-gran`:
 *    `https://www.vigorenergy.com.br/docs/boletos/<uc>/<arquivo>.pdf`. Público,
 *    permanente e no mesmo formato do boleto do Sicoob.
 * 2. `pdf_boleto_combinado` — o mesmo PDF no bucket PRIVADO `faturas`. Só serve
 *    de fallback para as faturas geradas antes da publicação no site (ou quando
 *    o upload falhou): abrir exige `createSignedUrl`, e o link resultante leva o
 *    token JWT do Storage colado na URL (`?token=eyJ...`) e morre em 5 minutos —
 *    não dá para compartilhar nem guardar.
 * 3. `url_boleto` — link da cobrança no gateway (Asaas nas parceiras, ou o PDF
 *    do Sicoob já hospedado no site nas faturas da Vigor).
 *
 * Devolve `null` quando não há nada para abrir; quem chama mostra o aviso.
 */

export type FaturaLinkBoleto = {
    id: string | number;
    url_boleto?: string | null;
    pdf_boleto_combinado?: string | null;
    url_boleto_combinado?: string | null;
    emitido_em?: string | null;
};

/** O boleto pode ser regerado; sem o parâmetro o navegador serve o PDF velho. */
function comVersao(url: string, chave: string | number): string {
    const separador = url.includes('?') ? '&' : '?';
    return `${url}${separador}v=${encodeURIComponent(String(chave))}`;
}

export async function resolverLinkBoleto(fatura: FaturaLinkBoleto | null | undefined): Promise<string | null> {
    if (!fatura) return null;
    const chave = fatura.emitido_em || fatura.id || Date.now();

    if (fatura.pdf_boleto_combinado || fatura.url_boleto_combinado) {
        // Buscado por id quando não veio no objeto: cada tela que abre os modais
        // de boleto seleciona um conjunto diferente de colunas — mesma razão pela
        // qual o `token_boleto_publico` é relido na hora do envio.
        let urlPublica = fatura.url_boleto_combinado ?? null;
        if (!urlPublica) {
            const { data } = await supabase
                .from('faturas')
                .select('url_boleto_combinado')
                .eq('id', fatura.id)
                .maybeSingle();
            urlPublica = data?.url_boleto_combinado ?? null;
        }
        if (urlPublica) return comVersao(urlPublica, chave);

        if (fatura.pdf_boleto_combinado) {
            const { data } = await supabase.storage
                .from('faturas')
                .createSignedUrl(fatura.pdf_boleto_combinado, 300);
            return data?.signedUrl ?? null;
        }
    }

    return fatura.url_boleto ? comVersao(fatura.url_boleto, chave) : null;
}
